// Phase I4.3 — Daily Acca · Direction C (Bloomberg Terminal).
// Faithful port of prototype/daily-acca.jsx::DailyAccaC.

import { Fragment } from "react";
import { ConfMeter } from "../../../components/super-acca";
import { EmptyAccaCard, EmptyFounderCard, LoadingCard } from "./EmptyStates.tsx";
import { legToVM } from "./legViewModel.ts";
import { marketLabel, selectionLabel } from "../../../exchange/market-format.ts";
import { summarizeReasoning } from "../../../agents/reasoning.ts";
import type { DailyAccaDirectionProps } from "./DailyAccaA.tsx";

const fontStat = "var(--sa-font-stat)";
const fontMono = "var(--sa-font-mono)";
const fontBody = "var(--sa-font-body)";

export function DailyAccaC({ acca, founder, recentAccaResult, isPro, loading }: DailyAccaDirectionProps): JSX.Element {
  return (
    <div style={{ padding: 0, fontFamily: fontMono, color: "var(--sa-fg-primary)", fontSize: 12 }}>
      {/* Ticker */}
      <div
        style={{
          background: "#000",
          borderBottom: "1px solid var(--sa-hairline)",
          padding: "8px 24px",
          display: "flex",
          gap: 28,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <span style={{ color: "var(--sa-accent-saffron)", letterSpacing: "0.18em", fontSize: 10 }}>DAILY WINDOW</span>
        {[
          [acca?.acca_date ?? "TODAY", "var(--sa-fg-primary)"],
          ["11:32 GMT", "var(--sa-fg-primary)"],
          ["18 AGENTS · LIVE", "var(--sa-signal-green)"],
          [isPro ? "PRO · 5-LEG" : "FREE · 3-LEG", "var(--sa-accent-saffron)"],
          ["1 FOUNDER", "var(--sa-accent-saffron)"],
        ].map(([k, c]) => (
          <span key={k} style={{ fontSize: 10.5, color: c, letterSpacing: "0.14em" }}>● {k}</span>
        ))}
      </div>

      {/* Header strip */}
      <div style={{ padding: "24px 28px", borderBottom: "1px solid var(--sa-hairline)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
          <div>
            <div style={{ fontSize: 10, letterSpacing: "0.22em", color: "var(--sa-fg-muted)" }}>
              {isPro ? "PRO" : "FREE_TIER"} · DAILY_ACCA · v1
            </div>
            <div style={{ fontFamily: fontStat, fontSize: 64, lineHeight: 0.9, color: "var(--sa-fg-primary)", marginTop: 8 }}>
              DAILY ACCA
            </div>
            <div style={{ fontSize: 12, color: "var(--sa-fg-secondary)", marginTop: 8, fontFamily: fontBody }}>
              {isPro ? "5-leg pro acca" : "3-leg free acca"} + Founder's Pick · refreshed daily 11:30 GMT
            </div>
          </div>
          {recentAccaResult && recentAccaResult.outcome ? (
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 10, color: "var(--sa-fg-muted)", letterSpacing: "0.18em" }}>RECENT_SETTLED</div>
              <div
                style={{
                  fontSize: 13,
                  color: recentAccaResult.outcome === "won" ? "var(--sa-signal-green)" : "var(--sa-fg-secondary)",
                  marginTop: 6,
                }}
              >
                ● {recentAccaResult.outcome.toUpperCase()} · {recentAccaResult.acca_date} @{" "}
                {recentAccaResult.combined_odds.toFixed(2)}×
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 28 }}><LoadingCard /></div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1.4fr 1fr",
            gap: 1,
            background: "var(--sa-hairline)",
            borderBottom: "1px solid var(--sa-hairline)",
          }}
        >
          {/* PANEL 1 — Free Acca legs */}
          <section style={{ background: "var(--sa-bg-base)", padding: 24 }}>
            {acca ? <AccaTable acca={acca} isPro={isPro} /> : <EmptyAccaCard />}
          </section>

          {/* PANEL 2 — Founder's Pick */}
          <section style={{ background: "#0E0B05", padding: 24 }}>
            {founder ? <FounderPanel founder={founder} /> : <EmptyFounderCard />}
          </section>
        </div>
      )}
    </div>
  );
}

function AccaTable({ acca, isPro }: { acca: NonNullable<DailyAccaDirectionProps["acca"]>; isPro: boolean }): JSX.Element {
  return (
    <>
      <PanelHead title={`${isPro ? "PRO" : "FREE"}_ACCA / ${acca.leg_count}_LEG`} right={`${acca.combined_odds.toFixed(2)}×`} />
      <div style={{ marginTop: 14, marginBottom: 14 }}>
        <ConfMeter value={acca.confidence ?? 0} locked={!isPro} label="COMPOSITE_CONFIDENCE" />
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11.5 }}>
        <thead>
          <tr style={{ color: "var(--sa-fg-muted)", textAlign: "left" }}>
            {["#", "FIXTURE", "MARKET", "SEL", "ODDS", "CONF"].map((h) => (
              <th
                key={h}
                style={{
                  padding: "8px 6px",
                  borderBottom: "1px solid var(--sa-hairline)",
                  fontSize: 9.5,
                  letterSpacing: "0.18em",
                  fontWeight: 500,
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {acca.legs.map((rawLeg, i) => {
            const leg = legToVM(rawLeg, i, acca.confidence);
            return (
              <Fragment key={`${rawLeg.fixture_id}|${i}`}>
                <tr style={{ borderBottom: "1px solid var(--sa-hairline)" }}>
                  <td style={{ padding: "12px 6px", color: "var(--sa-accent-saffron)" }}>L0{i + 1}</td>
                  <td style={{ padding: "12px 6px" }}>
                    <span style={{ color: "var(--sa-fg-primary)" }}>{leg.homeShort}</span>
                    <span style={{ color: "var(--sa-fg-muted)", margin: "0 6px" }}>×</span>
                    <span style={{ color: "var(--sa-fg-primary)" }}>{leg.awayShort}</span>
                    <span style={{ color: "var(--sa-fg-muted)", marginLeft: 10, fontSize: 10 }}>{leg.kickoff}</span>
                  </td>
                  <td style={{ padding: "12px 6px", color: "var(--sa-fg-secondary)" }}>{leg.market}</td>
                  <td style={{ padding: "12px 6px", color: "var(--sa-accent-saffron)" }}>{leg.selection}</td>
                  <td style={{ padding: "12px 6px" }}>{leg.odds.toFixed(2)}</td>
                  <td
                    style={{
                      padding: "12px 6px",
                      color: leg.legConf >= 75 ? "var(--sa-signal-green)" : "var(--sa-accent-saffron)",
                    }}
                  >
                    {isPro ? leg.legConf : "‧‧"}
                  </td>
                </tr>
                <tr>
                  <td
                    colSpan={6}
                    style={{
                      padding: "0 6px 14px 22px",
                      fontFamily: fontBody,
                      fontSize: 12,
                      color: "var(--sa-fg-muted)",
                      lineHeight: 1.5,
                      borderBottom: "1px solid var(--sa-hairline)",
                    }}
                  >
                    › {leg.reasoning}
                  </td>
                </tr>
              </Fragment>
            );
          })}
        </tbody>
      </table>
      <div style={{ marginTop: 16, display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--sa-fg-secondary)" }}>
        <span>
          STAKE 100 → RETURNS{" "}
          <span style={{ color: "var(--sa-accent-saffron)" }}>{Math.round(100 * acca.combined_odds)}</span>
        </span>
      </div>
    </>
  );
}

function FounderPanel({ founder }: { founder: NonNullable<DailyAccaDirectionProps["founder"]> }): JSX.Element {
  const reasoning = summarizeReasoning(founder.reasoning) ?? "";
  return (
    <>
      <PanelHead title="FOUNDER'S_PICK" right={`${founder.odds.toFixed(2)}×`} accent />
      <div style={{ fontSize: 10.5, color: "var(--sa-fg-muted)", letterSpacing: "0.14em", marginTop: 12 }}>
        {founder.fixture_id.toUpperCase()} · {founder.pick_date}
      </div>
      <div style={{ fontFamily: fontStat, fontSize: 38, color: "var(--sa-fg-primary)", lineHeight: 1, marginTop: 12 }}>
        {marketLabel(founder.market).toUpperCase()}
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginTop: 14 }}>
        <span style={{ fontFamily: fontStat, fontSize: 48, color: "var(--sa-accent-saffron)", lineHeight: 1 }}>
          {founder.odds.toFixed(2)}×
        </span>
        <span style={{ fontSize: 11, color: "var(--sa-fg-muted)" }}>{selectionLabel(founder.market, founder.selection)}</span>
      </div>
      {reasoning ? (
        <p style={{ fontFamily: fontBody, fontSize: 12.5, color: "var(--sa-fg-secondary)", marginTop: 14, lineHeight: 1.6 }}>
          {reasoning}
        </p>
      ) : null}
    </>
  );
}

function PanelHead({ title, right, accent }: { title: string; right: string; accent?: boolean }): JSX.Element {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        borderBottom: "1px solid var(--sa-hairline)",
        paddingBottom: 8,
      }}
    >
      <span style={{ fontSize: 10, letterSpacing: "0.22em", color: accent ? "var(--sa-accent-saffron)" : "var(--sa-fg-muted)" }}>
        ● {title}
      </span>
      <span style={{ fontSize: 11, color: accent ? "var(--sa-accent-saffron)" : "var(--sa-fg-primary)", letterSpacing: 0.4 }}>
        {right}
      </span>
    </div>
  );
}
